import { JsonArray, JsonObject } from '../../../../Assembler/dist/app/json/jsonValue.js';

/**
 * The AppSite-folder picker's SHARED, PURE parse/skip/resolve/build logic — the single source of truth for the
 * folder list, consumed identically by every tier.
 *
 * Design:
 * This class is deliberately DEPENDENCY-FREE: no file IO, no ConfigFile, no registry, no handler. It exists so
 * that no handler has to depend on another handler to reuse this logic — AppSiteFoldersData (server data
 * provider), AppSiteFoldersClientData (server raw preparer) and AppSiteFoldersClient (client processor) each
 * depend ONLY on this class, never on each other. Because it is pure it bundles into the browser untouched,
 * which is what lets the client processor run the byte-identical resolve in the browser, in --apiclient and in
 * --ociclient. Mirrors ScenariosFilter.
 */
export class AppSiteFoldersFilter {
    /**
     * Converts a raw folders JsonArray (folder-name strings, as produced by AppSiteFoldersData.getRawData /
     * AppSiteFoldersClientData.getClientData) into a folder-name list. Shared by the server processData and the
     * client processor so both parse the raw island/slice identically.
     */
    static parseFolders(rawFoldersArray: JsonArray | null): string[] {
        const folders: string[] = [];
        if (rawFoldersArray === null || rawFoldersArray === undefined) {
            return folders;
        }
        for (let i = 0; i < rawFoldersArray.length; i++) {
            const item = rawFoldersArray.get(i);
            folders.push(item !== null && item !== undefined ? item.toString() : '');
        }
        return folders;
    }

    /**
     * Applies the folder picker's STATIC skip: drop SkipViewAppSitesFolder folders (case-insensitive).
     * Shared by the server processData and the client preparer so the skip is byte-identical across tiers.
     */
    static filterFolders(folders: string[], skipViewFolders: string): string[] {
        if (!skipViewFolders) {
            return folders;
        }
        const skipFolderNames = skipViewFolders.split(',');
        const afterSkip: string[] = [];
        for (let i = 0; i < folders.length; i++) {
            let shouldSkip = false;
            for (let j = 0; j < skipFolderNames.length; j++) {
                if (folders[i].toLowerCase() === skipFolderNames[j].trim().toLowerCase()) {
                    shouldSkip = true;
                    break;
                }
            }
            if (!shouldSkip) {
                afterSkip.push(folders[i]);
            }
        }
        return afterSkip;
    }

    /**
     * Resolves the selected folder: a non-empty override (client SelectedAppSiteFolder) wins, else the default
     * folder; falls back to the first folder when the resolved value is empty or not in the list. Shared by the
     * server processData and the client processor.
     */
    static resolveSelectedFolder(folders: string[], defaultFolder: string, selectedOverride: string): string {
        let selectedFolder = selectedOverride || '';
        if (!selectedFolder && defaultFolder) {
            selectedFolder = defaultFolder;
        }
        if (!selectedFolder || !folders.includes(selectedFolder)) {
            selectedFolder = folders[0];
        }
        return selectedFolder;
    }

    /**
     * Builds the folder picker's result JsonObject (AppSiteFolders array of {Value, Label, IsDefault} + count +
     * the resolved SelectedAppSiteFolder). Shared by the server processData and the client processor.
     */
    static buildFoldersResult(folders: string[], selectedFolder: string): JsonObject {
        const retDataJson = new JsonObject();
        const foldersArray = new JsonArray();
        for (let i = 0; i < folders.length; i++) {
            const folder = folders[i];
            const folderObj = new JsonObject();
            folderObj.set('Value', folder);
            folderObj.set('Label', folder);
            folderObj.set('IsDefault', folder === selectedFolder);
            foldersArray.add(folderObj);
        }
        retDataJson.set('AppSiteFolders', foldersArray);
        retDataJson.set('AppSiteFoldersCount', foldersArray.length);
        retDataJson.set('SelectedAppSiteFolder', selectedFolder);
        return retDataJson;
    }
}
